import { SWRConfiguration } from 'swr';
import { toQuery } from '../components/Routes';
import { JsxArgs } from '../components/cms/CmsComponent';
import { CmsIndex } from '../utils/cmsIndex';
import {
	ApiAddressSuggestions,
	ApiBonus,
	ApiBonusActivate,
	ApiBonusBalance,
	ApiBonusRegisterData,
	ApiBrandMetaData,
	ApiBrandProducts,
	ApiCampaignProducts,
	ApiCart,
	ApiCategory,
	ApiCategoryMetaData,
	ApiCategoryProducts,
	ApiCategoryProductsLoop54,
	ApiCheckoutState,
	ApiCommunications,
	ApiCompanySearch,
	ApiCustomer,
	ApiDeliveryAddress,
	ApiDiscountCode,
	ApiEmailInfo,
	ApiGeneralProductsLoop54,
	ApiInvoiceAddresses,
	ApiList,
	ApiOrder,
	ApiOrderFulfillment,
	ApiOrderReturn,
	ApiOrderStats,
	ApiOrders,
	ApiPreviouslyBought,
	ApiPrinter,
	ApiPrinterModels,
	ApiProduct,
	ApiProductPage,
	ApiProductProducer,
	ApiProductsAo,
	ApiRedirect,
	ApiSchedule,
	ApiSearch,
	ApiSearchLoop54,
	ApiSimilarProducts,
	ApiStatisticsDataset,
	ApiStatisticsFilters,
	ApiStatisticsChart,
	ApiStatisticsCategories,
	ApiStatisticsConsumption,
	ApiStatisticsEco,
	ApiStatisticsSummary,
	ApiSpecialSuggestions,
	ApiSpecialsEvent,
	ApiSpecialsProduct,
	ApiSubAccount,
	ApiThanks,
	ApiTotalsParams,
	ApiUpdatePaymentPayload,
	ApiUser,
	RefreshProductDataCollection,
} from '../types';
import { ApiResource } from './api';

class ApiRes<T, TParams = {}> implements ApiResource<T, TParams> {
	constructor(
		public uri: (e: TParams) => string,
		public isPrivate: boolean,
		public swrConfig: Partial<SWRConfiguration> = {},
		public server: 'api' | 'cms' = 'api'
	) {}
	fetch(): T {
		throw new Error('not-implemented');
	}
}

const PRIVATE = true;
const PUBLIC = false;
type PageFilters = {
	price?: string;
	brand?: string;
	inStock?: string;
	campaign?: string;
	isEco?: string;
	bottomCategory?: string;
};
interface PageParams extends PageFilters {
	limit: number;
	offset: number;
}
type SortedPageParams = { limit: number; offset: number; sort: string };
type StatisticsParams = {
	startDate: string;
	endDate: string;
	productType: string;
	includeVat: boolean;
};
const noRevalidateExceptOnMount: SWRConfiguration = {
	revalidateOnFocus: false,
	revalidateOnMount: true,
	revalidateOnReconnect: false,
};
const quiet: SWRConfiguration = {
	revalidateIfStale: false,
	revalidateOnFocus: false,
	shouldRetryOnError: false,
};
const lazy: SWRConfiguration = {
	revalidateIfStale: false,
	dedupingInterval: 10 * 60 * 1000,
};
const manualcontrol: SWRConfiguration = {
	revalidateIfStale: false,
	dedupingInterval: 10 * 60 * 1000,
};

/**
 * These are Api Resource Objects. They represent api endpoints and are used as identifiers when dealing with api data.
 */
export const Res = {
	printerBrands: new ApiRes<Record<string, string>>(() => 'printers/brands.json', PUBLIC, lazy),

	printerModels: new ApiRes<ApiPrinterModels, { brandSlug: string }>(
		({ brandSlug }) => `printers/brands/${brandSlug}.json`,
		PUBLIC,
		lazy
	),

	printer: new ApiRes<ApiPrinter, { brandSlug: string; modelSlug: string }>(
		({ brandSlug, modelSlug }) => `printers/brands/${brandSlug}/${modelSlug}.json`,
		PUBLIC,
		lazy
	),

	brands: new ApiRes<Record<string, string>>(() => 'brands.json', PUBLIC, lazy),

	brandProducts: new ApiRes<ApiBrandProducts, { brandSlug: string } & SortedPageParams>(
		({ brandSlug, limit, offset, sort }) => `products.json?${toQuery({ brandSlug, limit, offset, sort })}`,
		PUBLIC,
		lazy
	),

	brandMetaData: new ApiRes<ApiBrandMetaData, { brandSlug: string }>(
		({ brandSlug }) => `brands/${brandSlug}.json`,
		PUBLIC,
		lazy
	),

	redirects: new ApiRes<ApiRedirect, { perma: string }>(({ perma }) => `redirects/${perma}.json`, PUBLIC, quiet),

	categories: new ApiRes<Record<string, ApiCategory>>(() => 'categories.json', PUBLIC, lazy),

	categoryMetaData: new ApiRes<ApiCategoryMetaData | ApiRedirect, { perma: string }>(
		({ perma }) => `categories/by-perma/${perma}.json`,
		PUBLIC,
		lazy
	),

	categoryProducts: new ApiRes<ApiCategoryProducts, { perma: string } & SortedPageParams>(
		({ perma, limit, offset, sort }) => {
			return `products.json?${toQuery({
				categoryPerma: perma,
				limit,
				offset,
				sort,
			})}`;
		},
		PUBLIC,
		lazy
	),

	campaignProducts: new ApiRes<ApiCampaignProducts, SortedPageParams>(
		({ limit, offset, sort }) => {
			return `products.json?campaign=true&&${toQuery({
				limit,
				offset,
				sort,
			})}`;
		},
		PUBLIC,
		lazy
	),

	viewedProducts: new ApiRes<ApiProduct[]>(
		() => {
			return 'products/viewed.json';
		},
		PRIVATE,
		lazy
	),

	cart: new ApiRes<ApiCart, ApiTotalsParams>(
		({ customerType, deliveryType, deliveryNotification, discountCode, letterInvoice }) =>
			`cart.json?${toQuery({
				customerType,
				deliveryType,
				deliveryNotification,
				discountCode,
				letterInvoice: letterInvoice ? 'true' : 'false',
			})}`,
		PRIVATE
	),

	favorites: new ApiRes<ApiProduct[]>(() => 'me/favorites/medium.json', PRIVATE, manualcontrol),

	favoriteIds: new ApiRes<number[]>(() => 'me/favorites.json', PRIVATE, manualcontrol),

	previouslyBought: new ApiRes<ApiPreviouslyBought>(() => 'me/bought-products.json', PRIVATE),

	deliveryAddresses: new ApiRes<ApiDeliveryAddress[]>(() => 'me/delivery-addresses.json', PRIVATE),

	productsAo: new ApiRes<ApiProductsAo, { letter: string }>(
		({ letter }) => `products/ao/${letter}.json`,
		PUBLIC,
		lazy
	),

	product: new ApiRes<ApiProductPage | ApiRedirect, { perma: string }>(
		({ perma }) => `products/by-perma/${perma}.json`,
		PUBLIC,
		lazy
	),
	productProducer: new ApiRes<ApiProductProducer, { tcId: number }>(
		({ tcId }) => `products/${tcId}/producer.json`,
		PUBLIC,
		lazy
	),

	searchProducts: new ApiRes<ApiSearch, { q?: string; server?: string; facetCategory?: string } & PageParams>(
		({ q, offset, limit, server, facetCategory }) => {
			return `products/search.json?${toQuery({
				q,
				offset,
				limit,
				server,
				facetCategory,
			})}`;
		},
		PUBLIC,
		lazy
	),

	searchProductsLoop54: new ApiRes<ApiSearchLoop54, PageParams & { q?: string; facetCategory?: string }>(
		({ limit, offset, q, facetCategory }) => `search&${toQuery({ q, limit, offset, facetCategory })}`,
		PUBLIC,
		manualcontrol
	),

	similarProductsLoop54: new ApiRes<ApiGeneralProductsLoop54, { limit: number; offset: number; productId: number }>(
		({ limit, offset, productId }) => `getRelatedEntities&${toQuery({ limit, offset, productId })}`,
		PUBLIC,
		manualcontrol
	),

	recommendedProductsLoop54: new ApiRes<ApiGeneralProductsLoop54, { productId: number }>(
		({ productId }) => `getRecommendedEntities&${toQuery({ productId })}`,
		PUBLIC,
		manualcontrol
	),

	campaignProductsLoop54: new ApiRes<ApiGeneralProductsLoop54, { take: number }>(
		({ take }) => `getEntitiesByAttribute?campaignStr=true&take=${take}`,
		PUBLIC,
		manualcontrol
	),

	topSellingProductsLoop54: new ApiRes<ApiGeneralProductsLoop54, { take: number }>(
		({ take }) => `getEntities?take=${take}`,
		PUBLIC,
		manualcontrol
	),

	productsLoop54: new ApiRes<ApiGeneralProductsLoop54, { productIds: string }>(
		({ productIds }) => `getEntities&${toQuery({ productIds })}`,
		PUBLIC,
		manualcontrol
	),

	complementaryProductsLoop54: new ApiRes<ApiGeneralProductsLoop54, { productId: number }>(
		({ productId }) => `getComplementaryEntities&${toQuery({ productId })}`,
		PUBLIC,
		manualcontrol
	),

	categoryIdProductsLoop54: new ApiRes<
		ApiGeneralProductsLoop54,
		{ categoryId: string; limit: number; offset: number }
	>(
		({ categoryId, limit, offset }) => `getEntitiesByAttribute&${toQuery({ categoryId, limit, offset })}`,
		PUBLIC,
		manualcontrol
	),

	categoryProductsLoop54: new ApiRes<ApiCategoryProductsLoop54, PageParams & { perma: string; sort?: string }>(
		({ limit, offset, perma, sort, price, brand, inStock, campaign, isEco, bottomCategory, ...restFilters }) => {
			const queryParams = toQuery({
				limit,
				offset,
				perma,
				sort,
				price,
				brand,
				inStock,
				campaign,
				isEco,
				...restFilters,
			});

			return `getEntitiesByAttribute&${queryParams}`;
		},
		PUBLIC,
		manualcontrol
	),

	brandProductsLoop54: new ApiRes<ApiCategoryProductsLoop54, PageParams & { brandSlug: string; sort?: string }>(
		({
			limit,
			offset,
			brandSlug,
			sort,
			price,
			brand,
			inStock,
			campaign,
			isEco,
			bottomCategory = '',
			...restFilters
		}) => {
			const queryParams = toQuery({
				limit,
				offset,
				brandSlug,
				sort,
				price,
				brand,
				inStock,
				campaign,
				isEco,
				bottomCategory,
				...restFilters,
			});

			return `getEntitiesByAttribute&${queryParams}`;
		},
		PUBLIC,
		manualcontrol
	),

	session: new ApiRes<ApiUser | boolean>(() => 'me.json', PRIVATE, manualcontrol),

	productsByIds: new ApiRes<{ products: ApiProduct[] }, { productIds: number[] } & PageParams>(
		({ productIds, offset, limit }) => {
			const ids = productIds.join(',');
			return `products.json?ids=${toQuery({ ids, offset, limit })}`;
		},
		PUBLIC,
		lazy
	),

	bonusActivate: new ApiRes<ApiBonusActivate>(() => 'me/bonus/activate.json', PRIVATE),

	bonusOrders: new ApiRes<ApiBonus[]>(() => 'me/bonus/orders.json', PRIVATE),

	bonusBalance: new ApiRes<ApiBonusBalance>(() => 'me/bonus/balance.json', PRIVATE),

	bonusRegisterData: new ApiRes<ApiBonusRegisterData>(() => 'me/bonus/get-bonus-register-data.json', PRIVATE),

	mostBought: new ApiRes<ApiProduct[]>(() => 'me/most-bought.json', PRIVATE),

	similarProducts: new ApiRes<ApiSimilarProducts, { product: number }>(
		({ product }) => `products/search/similar.json?product=${product}`,
		PUBLIC,
		lazy
	),

	order: new ApiRes<ApiOrder, { orderId: string }>(({ orderId }) => `me/orders/${orderId}.json`, PRIVATE),

	orderFulfillment: new ApiRes<ApiOrderFulfillment, { orderId: string }>(
		({ orderId }) => `me/orders/${orderId}/fulfillment.json`,
		PRIVATE
	),

	orders: new ApiRes<ApiOrders, PageParams>(
		({ offset, limit }) => `me/orders.json?offset=${offset}&limit=${limit}`,
		PRIVATE
	),

	orderStats: new ApiRes<ApiOrderStats>(() => `me/orderStats.json`, PRIVATE, quiet),

	statisticsDataset: new ApiRes<ApiStatisticsDataset>(() => 'me/statistics.json', PRIVATE, quiet),

	statisticsPresets: new ApiRes<ApiStatisticsFilters>(() => 'me/statistics/presets.json', PRIVATE, quiet),

	statisticsChart: new ApiRes<ApiStatisticsChart, StatisticsParams>(
		({ startDate, endDate, productType, includeVat }) =>
			`me/statistics/chart.json?${toQuery({
				startDate,
				endDate,
				productType,
				includeVat: includeVat ? 'true' : 'false',
			})}`,
		PRIVATE,
		quiet
	),

	statisticsCategories: new ApiRes<ApiStatisticsCategories, StatisticsParams>(
		({ startDate, endDate, productType, includeVat }) =>
			`me/statistics/categories.json?${toQuery({
				startDate,
				endDate,
				productType,
				includeVat: includeVat ? 'true' : 'false',
			})}`,
		PRIVATE,
		quiet
	),

	statisticsEco: new ApiRes<ApiStatisticsEco, StatisticsParams>(
		({ startDate, endDate, productType, includeVat }) =>
			`me/statistics/eco.json?${toQuery({
				startDate,
				endDate,
				productType,
				includeVat: includeVat ? 'true' : 'false',
			})}`,
		PRIVATE,
		quiet
	),

	statisticsConsumption: new ApiRes<ApiStatisticsConsumption, StatisticsParams>(
		({ startDate, endDate, productType, includeVat }) =>
			`me/statistics/consumption.json?${toQuery({
				startDate,
				endDate,
				productType,
				includeVat: includeVat ? 'true' : 'false',
			})}`,
		PRIVATE,
		quiet
	),

	statisticsSummary: new ApiRes<ApiStatisticsSummary, StatisticsParams>(
		({ startDate, endDate, productType, includeVat }) =>
			`me/statistics/summary.json?${toQuery({
				startDate,
				endDate,
				productType,
				includeVat: includeVat ? 'true' : 'false',
			})}`,
		PRIVATE,
		quiet
	),

	lists: new ApiRes<ApiList[]>(() => 'me/lists.json', PRIVATE),

	schedule: new ApiRes<ApiSchedule, { listId: string }>(({ listId }) => `me/lists/${listId}/schedule.json`, PRIVATE),

	specialsWithProducts: new ApiRes<ApiSpecialsProduct[], {}>(() => `me/specials-with-products.json`, PRIVATE),

	communications: new ApiRes<ApiCommunications[]>(() => 'mail/lists.json', PRIVATE, lazy),

	subscriptions: new ApiRes<number[]>(() => 'me/subscriptions.json', PRIVATE),

	subAccounts: new ApiRes<ApiSubAccount[]>(() => 'me/sub-accounts.json', PRIVATE),

	customer: new ApiRes<ApiCustomer>(() => 'me/medium.json', PRIVATE),

	invoiceAddresses: new ApiRes<ApiInvoiceAddresses>(() => 'me/invoice-addresses.json', PRIVATE),

	updatePayment: new ApiRes<ApiUpdatePaymentPayload>(() => 'me/payment.json', PRIVATE),

	cmsPage: new ApiRes<JsxArgs, { slug: string }>(({ slug }) => `pages/${slug}.json`, PUBLIC, quiet),

	cmsComponent: new ApiRes<JsxArgs, { slug: string }>(({ slug }) => `pieces/${slug}.json`, PUBLIC, quiet),

	brandHeader: new ApiRes<JsxArgs, { brand: string }>(({ brand }) => `brands/${brand}.json`, PUBLIC, quiet),

	/** Which pages, pieces, brands and categories the cms has published, rebuilt on every publish. */
	cmsIndex: new ApiRes<CmsIndex>(() => 'index.json', PUBLIC, quiet),

	categoryCmsContent: new ApiRes<JsxArgs, { perma: string }>(
		({ perma }) => `categories/${perma}.json`,
		PUBLIC,
		quiet
	),

	brandCmsContent: new ApiRes<JsxArgs, { brandSlug: string }>(
		({ brandSlug }) => `brands/products-banners/${brandSlug}.json`,
		PUBLIC,
		quiet
	),

	cmsEndpoint: new ApiRes<ApiCategoryProducts, { endpoint: string }>(({ endpoint }) => endpoint, PUBLIC, quiet),

	checkoutState: new ApiRes<ApiCheckoutState>(() => 'checkout.json', PRIVATE),

	thanks: new ApiRes<ApiThanks, { sid?: string }>(
		({ sid }) => {
			if (sid) {
				return `checkout/thanks.json?sid=${sid}`;
			}
			return 'checkout/thanks.json';
		},
		PRIVATE,
		noRevalidateExceptOnMount
	),

	klarnaCheckout: new ApiRes<string>(() => 'checkout/kco.html', PRIVATE),

	discountCode: new ApiRes<ApiDiscountCode>(() => 'check-coupon.json', PUBLIC, lazy),

	emailInfo: new ApiRes<ApiEmailInfo, { emailAddress: string }>(
		({ emailAddress }) => `checkout/email-info.json?email=${emailAddress}`,
		PUBLIC,
		lazy
	),

	companySearch: new ApiRes<ApiCompanySearch, { companyName: string }>(
		({ companyName }) => `checkout/company-search.json?company=${companyName}`,
		PUBLIC,
		lazy
	),

	peppolCheck: new ApiRes<unknown, { id: string }>(({ id }) => `peppol/${id}.json`, PUBLIC),

	addressSuggestionsCheck: new ApiRes<ApiAddressSuggestions, { street: string }>(
		({ street }) => `address-suggestions.json?q=${street}`,
		PUBLIC
	),

	refreshedProductData: new ApiRes<RefreshProductDataCollection, { productIds: number[] }>(
		({ productIds }) => `products/[${productIds.join(',')}]/stock-price.json`,
		PUBLIC
	),

	specialPricesSuggestions: new ApiRes<ApiSpecialSuggestions[], {}>(() => `me/specials/suggestions.json`, PRIVATE),

	specialsPriceEvents: new ApiRes<ApiSpecialsEvent[], {}>(() => `me/specials/messages.json`, PRIVATE),

	orderReturns: new ApiRes<ApiOrderReturn[], { orderId: string }>(
		({ orderId }) => `me/orders/${orderId}/returns.json`,
		PRIVATE,
		noRevalidateExceptOnMount
	),
};
